import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { usePage } from 'client/store'
import pageSelectors from 'client/store/page/pageSelectors'
import { Appearance } from 'common/types/entities/EntityInterface'
import { TextInterface } from 'common/types/entities/TextInterface'
import React from 'react'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import TextFontUpdater from '../components/Font/TextFontUpdater'
import { useUpdateEntity, useUpdateProp } from '../hooks/useUpdateProps'
import { DelayOption } from '../options'
import DeviceAppearance from '../options/DeviceAppearance/DeviceAppearance'
import HtmlAttrId from '../options/HtmlAttrId'
import LetterSpacing from '../styles/LetterSpacing'
import TextAlignStyle from '../styles/TextAlignStyle'
import TextFontSize from '../styles/TextFontSize'
import TextLineHeight from '../styles/TextLineHeight'

type TextSettingsProps = {
  entity: TextInterface
}

function TextSettingsNew({ entity }: TextSettingsProps) {
  const { isMobile } = useDeviceModeContext()
  const updateEntity = useUpdateEntity<TextInterface>()
  const updateProp = useUpdateProp(entity)

  const globalTextAlign = usePage(pageSelectors.getGlobalTextAlign)
  const globalMobileTextAlign = usePage(pageSelectors.getGlobalMobileTextAlign)

  function updateAppearance(appearance: Appearance) {
    updateEntity({
      ...entity,
      appearance,
    })
  }

  return (
    <>
      <GroupTitle isFirst>typography</GroupTitle>
      <TextFontSize
        update={updateProp('fontSize')}
        mobileUpdate={updateProp('mobileFontSize')}
        fontSize={entity.fontSize}
        mobileFontSize={entity.mobileFontSize}
        removeEntityStyle={updateProp('fontSize')}
        removeEntityMobileStyle={updateProp('mobileFontSize')}
      />
      <TextLineHeight
        fontSize={entity.fontSize}
        mobileFontSize={entity.mobileFontSize}
        update={updateProp('lineHeight')}
        mobileUpdate={updateProp('mobileLineHeight')}
        lineHeight={entity.lineHeight}
        mobileLineHeight={entity.mobileLineHeight}
        removeEntityStyle={updateProp('lineHeight')}
        removeEntityMobileStyle={updateProp('mobileLineHeight')}
      />
      <TextFontUpdater<TextInterface> entity={entity} update={updateEntity} />
      <LetterSpacing
        change={updateProp(isMobile ? 'mobileLetterSpacing' : 'letterSpacing')}
        letterSpacing={
          isMobile ? entity.mobileLetterSpacing : entity.letterSpacing
        }
      />
      <GroupTitle>color</GroupTitle>
      <ColorPickerControl
        update={updateProp('backgroundColor')}
        mobileUpdate={updateProp('mobileBackgroundColor')}
        color={entity.backgroundColor}
        mobileColor={entity.mobileBackgroundColor}
        label="entity_settings.text.background_color"
        isClearable
      />
      <SizeAndPositionGroup
        padding={isMobile ? entity?.mobilePadding : entity.padding}
        updatePadding={updateProp(isMobile ? 'mobilePadding' : 'padding')}
        margin={isMobile ? entity.mobileMargin : entity.margin}
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
      />
      <TextAlignStyle
        textAlign={entity.textAlign || globalTextAlign}
        mobileTextAlign={
          entity.mobileTextAlign || entity.textAlign || globalMobileTextAlign
        }
        update={updateProp('textAlign')}
        mobileUpdate={updateProp('mobileTextAlign')}
        isResetIconVisible={
          !!(isMobile ? entity.mobileTextAlign : entity.textAlign)
        }
        removeEntityStyle={updateProp('textAlign')}
        removeEntityMobileStyle={updateProp('mobileTextAlign')}
      />
      <DelayOption update={updateProp('delay')} delay={entity.delay} />
      <DeviceAppearance
        update={updateAppearance}
        desktop={entity.appearance.desktop}
        mobile={entity.appearance.mobile}
      />
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </>
  )
}

export default TextSettingsNew
